import { buzz, tick } from './haptic'
import { countBetween, reducedMotion } from './motion'
import { money } from './ui'
import './paid-moment.css'

// Marking paid, made satisfying (Liam, 9 Oct 2026: "we are marking of a payment which should apike dopamine"; approved
// "B" from the try-it page https://claude.ai/artifact/HuCqHD7wE39UbbmWkXq2gm, with Cash and Bank in their own colours).
// One moment for every place a payment is marked (Owed's rows and card, the Diary's "Paid: Cash / Bank", "How was it
// paid?"), so it feels the same everywhere:
//   - Cash pale lemon with a banknote, Bank pale slate with a bank, wherever they are buttons (Liam picked D);
//   - the tap: green spreads across the row from the button, a tick, a short buzz, a coin sound if switched on;
//   - on Owed: the amount flies into "Paid today", which counts up; the last one paid shows "All paid up";
//   - on an Owed row on a phone: slide right for Cash, left for Bank (the buttons stay).
// Every part does nothing on a phone set to reduce motion except the words, which always change. The moment never
// says a save happened that did not: the caller plays it on the tap and takes it back if the save is refused.

export type PaidWay = 'cash' | 'bank'

const ICONS: Record<PaidWay, string> = {
  cash: '<svg class="paid-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="2.5" y="6" width="19" height="12" rx="2"/><circle cx="12" cy="12" r="2.6"/></svg>',
  bank: '<svg class="paid-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9.5 12 4l9 5.5M5 10v8M9.7 10v8M14.3 10v8M19 10v8M3 20h18"/></svg>',
}

// Drawings are decoration: a page that cannot add them (an older phone's browser) keeps the words, which say it all.
function addDrawing(node: Element, where: InsertPosition, html: string): void {
  if (typeof node.insertAdjacentHTML === 'function') node.insertAdjacentHTML(where, html)
}

// Cash or Bank in its own colour, with its picture before the word. The word stays the button's name.
export function paidLook(button: HTMLElement, way: PaidWay): void {
  button.classList.add('paid-way', `paid-${way}`)
  if (!button.querySelector('.paid-icon')) addDrawing(button, 'afterbegin', ICONS[way])
}

// The coin sound: off unless this phone switched it on (More, "Coin sound when marked paid").
const SOUND_KEY = 'roundly:paid-sound'
export function coinSoundOn(): boolean {
  try { return localStorage.getItem(SOUND_KEY) === 'on' } catch { return false }
}
export function setCoinSound(on: boolean): void {
  try { if (on) localStorage.setItem(SOUND_KEY, 'on'); else localStorage.removeItem(SOUND_KEY) } catch { /* this phone keeps no settings */ }
}
let audio: AudioContext | null = null
export function coin(): void {
  if (!coinSoundOn()) return
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    audio ??= new Ctx()
    if (audio.state === 'suspended') void audio.resume()
    const t = audio.currentTime
    for (const [f, d] of [[1318.5, 0], [1760, 0.075]] as const) {
      const o = audio.createOscillator(), g = audio.createGain()
      o.type = 'triangle'; o.frequency.value = f
      g.gain.setValueAtTime(0.0001, t + d); g.gain.exponentialRampToValueAtTime(0.2, t + d + 0.012); g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.38)
      o.connect(g).connect(audio.destination); o.start(t + d); o.stop(t + d + 0.42)
    }
  } catch { /* no sound on this phone */ }
}

const TICK_SVG = '<svg class="paid-tick" viewBox="0 0 44 44" aria-hidden="true"><path d="M13 23l6 6 12-13"/></svg>'

// The tap: host turns green from `from` (the button pressed), a tick takes the place of `mark` (the initials or the
// price), `line` says what was saved, the phone buzzes and, if switched on, a coin sounds. Call from the tap itself (a
// phone only buzzes for a person's tap). Returns how to take it back if the save is refused.
export function paidMoment(o: { host: HTMLElement; from?: HTMLElement | null; way: PaidWay; line?: HTMLElement | null; words?: string; mark?: HTMLElement | null }): () => void {
  buzz('success')
  coin()
  const r = o.host.getBoundingClientRect()
  const f = o.from?.getBoundingClientRect()
  if (typeof o.host.style?.setProperty === 'function') {
    o.host.style.setProperty('--paid-x', `${f ? f.left + f.width / 2 - r.left : r.width / 2}px`)
    o.host.style.setProperty('--paid-y', `${f ? f.top + f.height / 2 - r.top : r.height / 2}px`)
  }
  o.host.classList.add('paid-host', 'paid-now', `paid-now-${o.way}`)
  const before = o.line?.textContent ?? null
  if (o.line) { o.line.textContent = o.words ?? `Paid · ${o.way === 'cash' ? 'Cash' : 'Bank'} · just now`; o.line.classList.add('paid-line') }
  let tickEl: HTMLElement | null = null
  if (o.mark) {
    o.mark.classList.add('paid-mark')
    addDrawing(o.mark, 'beforeend', TICK_SVG)
    tickEl = o.mark.querySelector('.paid-tick')
  }
  return () => {
    o.host.classList.remove('paid-now', `paid-now-${o.way}`)
    if (o.line && before !== null) { o.line.textContent = before; o.line.classList.remove('paid-line') }
    o.mark?.classList.remove('paid-mark')
    tickEl?.remove()
  }
}

// ---- Not paid yet (tidy-up item 2; Liam, 20 Oct 2026: "the not paid yet doesnt have a color also other parts of the
// app have similar with better ui animations"; docs/plans/tidy-up/AGREED.md section 2) ---------------------------------
// Its own look: orange with a clock, wherever it is a payment answer, so it no longer reads as a second Paid cash. Its
// own moment where Paid has one: orange spreads from the button on the tap (taken back if the save is refused), and
// once the server has answered, "Added to Owed · £X" from what it saved. Never a word about Owed before then.

const CLOCK = '<svg class="paid-icon owed-clock" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.2 2"/></svg>'

// The big "Not paid yet" button (the "How was it paid?" pop-up): orange, its clock before the words.
export function owedLook(button: HTMLElement): void {
  button.classList.add('owed-way')
  if (!button.querySelector('.owed-clock')) addDrawing(button, 'afterbegin', CLOCK)
}

// The tap: orange spreads across `host` from `from`, and a light buzz (a choice, not money in: no coin). Returns how to
// take it back if the save is refused. Nothing moves on a phone set to reduce motion.
export function owedMoment(o: { host: HTMLElement; from?: HTMLElement | null }): () => void {
  buzz('light')
  if (reducedMotion()) return () => {}
  const r = o.host.getBoundingClientRect()
  const f = o.from?.getBoundingClientRect()
  if (typeof o.host.style?.setProperty === 'function') {
    o.host.style.setProperty('--paid-x', `${f ? f.left + f.width / 2 - r.left : r.width / 2}px`)
    o.host.style.setProperty('--paid-y', `${f ? f.top + f.height / 2 - r.top : r.height / 2}px`)
  }
  o.host.classList.add('paid-host', 'paid-now', 'paid-now-owed')
  return () => o.host.classList.remove('paid-now', 'paid-now-owed')
}

// The words once the server has answered: "Added to Owed · £200.19" / "Wanda owes you. Owed will remind you." (the
// approved drawing), or anything else said the same way (waiting for signal, taken off the round).
export function owedBanner(title: string, line: string): HTMLElement {
  const box = document.createElement('div')
  box.className = 'owed-added'
  box.setAttribute('role', 'status')
  const disc = document.createElement('span')
  disc.className = 'owed-added-disc'
  disc.setAttribute('aria-hidden', 'true')
  addDrawing(disc, 'afterbegin', CLOCK)
  const words = document.createElement('span')
  words.className = 'owed-added-words'
  const strong = document.createElement('strong')
  strong.textContent = title
  const small = document.createElement('span')
  small.textContent = line
  words.append(strong, small)
  box.append(disc, words)
  return box
}

// The amount flies from `from` into Owed's "Paid today", which then counts up to its new figure.
export function flyToPaidToday(from: HTMLElement, pence: number): void {
  const target = document.querySelector<HTMLElement>('#owed-paid-today strong')
  if (!target) return
  // Paid today hides while nothing is paid (Owed design D); the first payment shows it before the coin flies to it.
  target.closest('.owed-paid-today')?.classList.remove('is-zero')
  const was = Number(target.dataset.pence ?? '0')
  const now = was + pence
  target.dataset.pence = String(now)
  const land = (): void => {
    countBetween(target, (k) => money(Math.round(was + (now - was) * k)), 460)
    if (!reducedMotion()) target.animate?.([{ transform: 'scale(1)' }, { transform: 'scale(1.22)', offset: 0.4 }, { transform: 'scale(1)' }], { duration: 420, easing: 'cubic-bezier(.2,.9,.3,1.4)' })
  }
  const a = from.getBoundingClientRect(), b = target.getBoundingClientRect()
  if (reducedMotion() || typeof document.body.animate !== 'function' || !a.width) { land(); return }
  const chip = document.createElement('span')
  chip.className = 'paid-coin'
  chip.textContent = `+${money(pence)}`
  chip.style.left = `${a.left}px`
  chip.style.top = `${a.top}px`
  document.body.append(chip)
  const dx = b.right - chip.offsetWidth - a.left, dy = b.top - a.top
  const flight = chip.animate([
    { transform: 'translate(0, 0) scale(1)', opacity: 1 },
    { transform: `translate(${dx * 0.3}px, ${dy * 0.5 - 36}px) scale(1.18)`, opacity: 1, offset: 0.45 },
    { transform: `translate(${dx}px, ${dy}px) scale(.55)`, opacity: 0.15 },
  ], { duration: 680, easing: 'cubic-bezier(.3,.7,.3,1)' })
  flight.onfinish = () => { chip.remove(); land() }
  flight.oncancel = () => { chip.remove(); land() }
}

// The last one paid: a tick, "All paid up", the day's figure and a small lemon and green burst.
export function allPaidUp(into: HTMLElement, pencePaidToday: number | null): HTMLElement {
  const box = document.createElement('div')
  box.className = 'paid-all'
  box.setAttribute('role', 'status')
  addDrawing(box, 'afterbegin', '<svg viewBox="0 0 76 76" aria-hidden="true"><circle cx="38" cy="38" r="36"/><path d="M23 39l10 10 21-22"/></svg>')
  const words = document.createElement('strong')
  words.textContent = 'All paid up'
  box.append(words)
  if (pencePaidToday && pencePaidToday > 0) {
    const day = document.createElement('span')
    day.textContent = `${money(pencePaidToday)} paid today`
    box.append(day)
  }
  into.append(box)
  tick()
  if (!reducedMotion()) burst(into)
  return box
}
function burst(into: HTMLElement): void {
  const c = document.createElement('canvas')
  c.className = 'paid-burst'
  const w = into.clientWidth || 360, h = Math.max(260, into.clientHeight)
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  c.width = w * dpr; c.height = h * dpr
  into.append(c)
  const x = c.getContext('2d')
  if (!x || typeof requestAnimationFrame !== 'function') { c.remove(); return }
  x.scale(dpr, dpr)
  const colours = ['#f4dc52', '#1f7a3f', '#5fa776', '#2b3440', '#c4ab25']
  const cx = w / 2, cy = Math.min(h - 60, 120)
  const bits = Array.from({ length: 60 }, () => {
    const ang = Math.random() * Math.PI * 2, sp = 3 + Math.random() * 6
    return { x: cx, y: cy, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp - 4, r: 3 + Math.random() * 4, c: colours[Math.floor(Math.random() * colours.length)]!, rot: Math.random() * 6, vr: (Math.random() - 0.5) * 0.4 }
  })
  const t0 = performance.now()
  const step = (t: number): void => {
    const k = (t - t0) / 1200
    x.clearRect(0, 0, w, h)
    for (const b of bits) {
      b.vy += 0.22; b.vx *= 0.985; b.x += b.vx; b.y += b.vy; b.rot += b.vr
      x.save(); x.globalAlpha = Math.max(0, 1 - k); x.translate(b.x, b.y); x.rotate(b.rot); x.fillStyle = b.c; x.fillRect(-b.r, -b.r / 2, b.r * 2, b.r); x.restore()
    }
    if (k < 1 && c.isConnected) requestAnimationFrame(step)
    else c.remove()
  }
  requestAnimationFrame(step)
}

// Slide to pay, on an Owed row on a phone: the whole row moves under the finger, one piece; the colour and the word
// show behind it (right Cash, left Bank); one buzz past the point of no return; let go there and it pays, let go before
// and it springs back with nothing saved. A slide that is more up and down than across is a scroll, left alone.
function underFor(row: HTMLElement, amount: string): HTMLElement | null {
  const list = row.parentElement
  if (!list) return null
  if (getComputedStyle(list).position === 'static') list.style.position = 'relative'
  const under = document.createElement('div')
  under.className = 'paid-under'
  under.setAttribute('aria-hidden', 'true')
  under.style.top = `${row.offsetTop}px`
  under.style.height = `${row.offsetHeight}px`
  // The word only ("Cash", then "Let go" from halfway): the row already shows the amount, and a long label is covered by
  // the row before it can be read (Liam's look, 10 Oct 2026).
  const side = (way: PaidWay, words: string): HTMLElement => {
    const span = document.createElement('span')
    span.className = `paid-under-${way}`
    const word = document.createElement('b')
    word.className = 'paid-word'
    word.textContent = words
    span.append(word)
    addDrawing(span, way === 'cash' ? 'afterbegin' : 'beforeend', ICONS[way])
    return span
  }
  under.dataset.amount = amount
  under.append(side('cash', 'Cash'), side('bank', 'Bank'))
  list.insertBefore(under, row)
  return under
}

export function slideToPay(row: HTMLElement, amount: string, pay: (way: PaidWay, from: HTMLElement) => void): void {
  row.classList.add('paid-slide-row')
  let start: { x: number; y: number; id: number } | null = null
  let dragging = false
  let armed = 0
  let under: HTMLElement | null = null
  const move = (px: number, animate: boolean): void => {
    row.style.transition = animate ? 'transform 260ms cubic-bezier(.2,.9,.25,1.3)' : ''
    row.style.transform = px ? `translateX(${px}px)` : ''
  }
  const finish = (): void => {
    row.classList.remove('paid-sliding', 'paid-dragging')
    under?.remove()
    under = null
  }
  row.addEventListener('pointerdown', (e) => {
    // A slide can start anywhere on the row, its own buttons included (a tap still taps: a slide swallows only the
    // click straight after it); never from a box you type in, a link, an open menu or the "Looks like a business"
    // question (a slide there would mark it paid: Astra 15, 26 Oct 2026).
    if (e.button !== 0 || row.classList.contains('cell-pending') || (e.target as Element).closest('a, input, select, textarea, [role="menuitem"], [role="menu"], .owed-business-area')) return
    start = { x: e.clientX, y: e.clientY, id: e.pointerId }
    dragging = false
    armed = 0
  })
  row.addEventListener('pointermove', (e) => {
    if (!start || e.pointerId !== start.id) return
    const dx = e.clientX - start.x, dy = e.clientY - start.y
    if (!dragging) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        dragging = true
        try { row.setPointerCapture(e.pointerId) } catch { /* the slide still follows */ }
        under?.remove()
        under = underFor(row, amount)
        row.classList.add('paid-sliding', 'paid-dragging')
      } else if (Math.abs(dy) > 10) { start = null; return } else return
    }
    const w = row.offsetWidth || 1, t = w * 0.4, d = Math.abs(dx)
    // Past halfway the row gives less, so it feels held and you know you can let go; the word fades in as you go.
    move(Math.sign(dx) * Math.min(d <= t ? d : t + (d - t) * 0.45, w * 0.9), false)
    under?.style.setProperty('--paid-p', String(Math.min(1, 0.35 + (d / t) * 0.65)))
    under?.classList.toggle('paid-to-cash', dx > 0)
    under?.classList.toggle('paid-to-bank', dx < 0)
    const now = d > t ? Math.sign(dx) : 0
    if (now !== armed) {
      armed = now
      under?.classList.toggle('paid-armed', !!armed)
      row.classList.toggle('paid-armed', !!armed)
      // Halfway: the word becomes "Let go" and pops, the phone buzzes (the iPhone app; Android; an iPhone web page cannot).
      const word = under?.querySelector<HTMLElement>(dx > 0 ? '.paid-under-cash .paid-word' : '.paid-under-bank .paid-word')
      if (word) word.textContent = armed ? 'Let go' : dx > 0 ? 'Cash' : 'Bank'
      under?.classList.remove('paid-snap')
      if (armed) { void under?.offsetWidth; under?.classList.add('paid-snap'); buzz('medium') }
    }
  })
  const end = (cancelled: boolean): void => {
    if (!start) return
    const was = dragging
    start = null
    dragging = false
    if (!was) return
    // A slide is not a tap: the row's own click (open its card) must not follow it. Only the click that comes straight
    // after the slide; a phone that sends none leaves the next real tap alone.
    const swallow = (e: Event): void => { e.stopPropagation(); e.preventDefault() }
    row.addEventListener('click', swallow, { capture: true, once: true })
    window.setTimeout(() => row.removeEventListener('click', swallow, { capture: true }), 400)
    row.classList.remove('paid-dragging', 'paid-armed')
    const paying = armed && !cancelled ? (armed > 0 ? 'cash' : 'bank') as PaidWay : null
    armed = 0
    const from = under?.querySelector<HTMLElement>(paying === 'bank' ? '.paid-under-bank' : '.paid-under-cash') ?? row
    move(0, true)
    window.setTimeout(finish, 280)
    if (paying) pay(paying, from)
  }
  row.addEventListener('pointerup', () => end(false))
  row.addEventListener('pointercancel', () => end(true))
}

// The first time a phone shows rows that slide: the top one nudges twice to show it, then never again on this phone.
const NUDGE_KEY = 'roundly:paid-slide-shown'
export function nudgeSlideOnce(row: HTMLElement, amount: string): void {
  try { if (localStorage.getItem(NUDGE_KEY)) return; localStorage.setItem(NUDGE_KEY, '1') } catch { return }
  if (reducedMotion()) return
  window.setTimeout(() => {
    if (!row.isConnected || typeof row.animate !== 'function') return
    const under = underFor(row, amount)
    under?.classList.add('paid-to-cash')
    row.classList.add('paid-sliding')
    const a = row.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(64px)', offset: 0.4 }, { transform: 'translateX(0)' }], { duration: 1200, iterations: 2, easing: 'ease-in-out' })
    const done = (): void => { under?.remove(); row.classList.remove('paid-sliding') }
    a.onfinish = done
    a.oncancel = done
  }, 600)
}
